import { useRecoilTask, type RecoilTaskInterface } from 'recoil-toolkit';

import { userState } from '../../../state/user';
import type { EventSettings } from '../../../types';
import { eventSettingService } from '../api/eventSettings';

export type EventSettingsGridKey = 'incidentGridSettings' | 'drsGridSettings' | 'pmpGridSettings';
export type EventSettingsGridType = 'incident' | 'drs' | 'pmp';

const gridKeyMap: Record<EventSettingsGridType, EventSettingsGridKey> = {
  incident: 'incidentGridSettings',
  drs: 'drsGridSettings',
  pmp: 'pmpGridSettings',
};

const loaderStackMap: Record<EventSettingsGridType, string> = {
  incident: 'incidentGridSettingsUpdate',
  drs: 'dailyTaskGridSettingsUpdate',
  pmp: 'projectTaskGridSettingsUpdate',
};

const toggleCompactModeTask =
  (gridType: EventSettingsGridType) =>
  ({ getLoadable, set }: RecoilTaskInterface) =>
  async () => {
    const currentUser = getLoadable(userState).getValue();
    const currentEventSetting = currentUser.eventSettings.find(
      (eventSetting) => eventSetting.event.id === currentUser.currentEvent.id,
    ) as EventSettings;
    const gridKey = gridKeyMap[gridType];
    const currentGridSettings = currentEventSetting[gridKey];
    const nextViewMode = currentGridSettings.viewMode === 'compact' ? 'default' : 'compact';

    const { data } = await eventSettingService.update({
      id: currentEventSetting.id,
      [gridKey]: {
        ...currentGridSettings,
        viewMode: nextViewMode,
      },
    });

    set(userState, (state) => ({
      ...state,
      eventSettings: state.eventSettings.map((eventSetting) => {
        if (eventSetting.id === data.data.id) {
          return {
            ...eventSetting,
            ...data.data,
          };
        }

        return eventSetting;
      }),
    }));
  };

export const useToggleCompactMode = (gridType: EventSettingsGridType) =>
  useRecoilTask(toggleCompactModeTask(gridType), [gridType], {
    loaderStack: loaderStackMap[gridType],
    errorStack: true,
  });
